// Mind2Magic CRM — Duplicate Scanner Module
// Extracted from index.html Step 10.

import { STAGES } from './constants.js'
import { store } from './store.js'

// Dismissed pairs persistence via localStorage
const DISMISSED_KEY = 'm2m_dup_dismissed'
function _getDismissed() {
    try { return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]')) } catch(e) { return new Set() }
}
function _saveDismissed(set) {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...set]))
}
function _pairKey(a, b) {
    return [a.id, b.id].sort().join('|')
}

function scanDuplicates(fullScan) {
    const { leads, historico } = store
    const allLeads = [...leads, ...historico]
    if (!allLeads.length) { window.toast('No hay leads para escanear', 'info'); return }
    const groups = _rescanDuplicates()
    // Filter out dismissed pairs (unless full scan)
    const dismissed = _getDismissed()
    const filtered = fullScan ? groups : groups.filter(g => !dismissed.has(_pairKey(g.leads[0], g.leads[1])))
    if (!filtered.length && groups.length > 0) {
        window.toast(`${groups.length} par(es) ya descartados — usa "Escaneo Completo" para revisarlos`, 'info')
        return
    }
    showDupScanner(filtered, fullScan)
}

function showDupScanner(groups, fullScan) {
    // Remove existing
    const existing = document.getElementById('dupScannerOverlay')
    if (existing) existing.remove()

    if (!groups.length) {
        window.toast('No se encontraron duplicados', 'success')
        return
    }

    const highCount = groups.filter(g => g.confidence === 'high').length
    const superCount = groups.filter(g => _isSuperMergeCandidate(g)).length
    const overlay = document.createElement('div')
    overlay.className = 'dup-scanner-overlay'
    overlay.id = 'dupScannerOverlay'
    overlay.innerHTML = `
        <div class="dup-scanner">
            <div class="dup-scanner-header">
                <h3>Duplicados Encontrados (${groups.length})</h3>
                <button class="btn btn-ghost btn-sm" onclick="closeDupScanner()" style="font-size:20px;">&times;</button>
            </div>
            <div class="dup-scanner-search">
                <input type="text" id="dupSearchInput" placeholder="Buscar por nombre, destino, teléfono, email..." oninput="filterDupGroups(this.value)" autocomplete="off">
                <span id="dupSearchCount" style="display:none;font-size:12px;color:var(--text-3);margin-top:4px;"></span>
            </div>
            <div class="dup-scanner-body" id="dupScannerBody">
                ${groups.map((g, idx) => renderDupGroup(g, idx)).join('')}
            </div>
            <div class="dup-scanner-footer">
                <span style="font-size:12px;color:var(--text-3);">${groups.length} grupo(s) — ${highCount} alta confianza, ${superCount} super fusión, ${groups.length - highCount - superCount} otro — <span id="dupScannerRemaining">${groups.length} pendiente(s)</span></span>
                <div style="display:flex;gap:8px;flex-wrap:wrap;">
                    ${superCount > 0 ? `<button class="btn btn-sm btn-warning" onclick="superMergeAll()">⚡ Super Fusionar (${superCount})</button>` : ''}
                    ${highCount > 0 ? `<button class="btn btn-sm btn-success" onclick="mergeHighConfidence()">Fusionar Alta Confianza (${highCount})</button>` : ''}
                    ${groups.length > 1 ? `<button class="btn btn-sm btn-primary" onclick="mergeAllDuplicates()">Fusionar Todos (${groups.length})</button>` : ''}
                    ${!fullScan ? `<button class="btn btn-sm btn-ghost" onclick="scanDuplicates(true)" title="Incluye pares descartados anteriormente">🔍 Escaneo Completo</button>` : ''}
                    <button class="btn btn-sm btn-ghost" onclick="closeDupScanner()">Cerrar</button>
                </div>
            </div>
        </div>`
    document.body.appendChild(overlay)
    window._dupGroups = groups
}

function _pickKeepDiscard(a, b) {
    const orderMap = {}
    STAGES.forEach((s, i) => orderMap[s.id] = i)
    const aOrder = orderMap[a.stage] ?? -1
    const bOrder = orderMap[b.stage] ?? -1
    const keep = aOrder >= bOrder ? a : b
    const discard = keep === a ? b : a
    return [keep, discard]
}

function swapDupDirection(idx) {
    const group = window._dupGroups?.[idx]
    if (!group || group._resolved) return
    group._swapped = !group._swapped
    const el = document.getElementById('dupGroup' + idx)
    if (el) el.outerHTML = renderDupGroup(group, idx)
}

function renderDupGroup(group, idx) {
    const [origA, origB] = group.leads
    const [autoKeep, autoDiscard] = _pickKeepDiscard(origA, origB)
    const keeper = group._swapped ? autoDiscard : autoKeep
    const discard = group._swapped ? autoKeep : autoDiscard

    const keeperStage = STAGES.find(s => s.id === keeper.stage)?.name || keeper.stage
    const discardStage = STAGES.find(s => s.id === discard.stage)?.name || discard.stage
    const TYPE_LABELS = { disney: 'Disney', crucero: 'Crucero', hotel: 'Hotel', explorando: 'Explorando', otro: 'Otro' }
    const isSuper = _isSuperMergeCandidate(group)

    const pick = (a, b) => (a && a !== 'Sin destino') ? a : b
    const pickNum = (a, b) => (a || 0) > 0 ? a : b

    const pv = (label, keepVal, discardVal, isMoney) => {
        const kv = keepVal || ''
        const dv = discardVal || ''
        const kDisplay = isMoney ? '$' + ((keepVal || 0).toLocaleString()) : (kv || '--')
        const dDisplay = isMoney ? '$' + ((discardVal || 0).toLocaleString()) : (dv || '--')
        let resultDisplay, source
        if (isMoney) {
            if (isSuper) {
                const sum = (keepVal || 0) + (discardVal || 0)
                resultDisplay = '$' + sum.toLocaleString()
                source = (keepVal || 0) > 0 && (discardVal || 0) > 0 ? 'summed' : (keepVal || 0) > 0 ? 'keeper' : 'discard'
            } else {
                const best = pickNum(keepVal, discardVal)
                resultDisplay = '$' + (best || 0).toLocaleString()
                source = (keepVal || 0) > 0 ? 'keeper' : (discardVal || 0) > 0 ? 'discard' : 'empty'
            }
        } else {
            const picked = pick(kv, dv)
            resultDisplay = picked || '--'
            source = picked === kv && kv ? 'keeper' : picked === dv && dv ? 'discard' : 'empty'
        }
        return { label, kDisplay, dDisplay, resultDisplay, source, differs: kDisplay !== dDisplay }
    }

    const fields = [
        { label: 'Etapa', kDisplay: keeperStage, dDisplay: discardStage, resultDisplay: keeperStage, source: 'keeper', differs: keeperStage !== discardStage },
        pv('Tipo', TYPE_LABELS[keeper.type] || keeper.type, TYPE_LABELS[discard.type] || discard.type, false),
        pv('Destino', keeper.destination, discard.destination, false),
        pv('Fecha', keeper.travelDate ? window.fmtDate(keeper.travelDate) : '', discard.travelDate ? window.fmtDate(discard.travelDate) : '', false),
        pv('Telefono', keeper.phone, discard.phone, false),
        pv('Email', keeper.email, discard.email, false),
        pv('Venta', keeper.totalVenta, discard.totalVenta, true),
        pv('Comision', keeper.commission, discard.commission, true)
    ]

    const sourceColor = (src) => {
        if (src === 'keeper') return 'var(--green)'
        if (src === 'discard') return 'var(--blue, #3B82F6)'
        if (src === 'summed') return 'var(--yellow)'
        return 'var(--text-3)'
    }

    const confLabel = group.confidence === 'high' ? 'Alta confianza' : group.confidence === 'manual' ? 'Manual' : 'Posible'
    const confClass = group.confidence === 'high' ? 'high' : group.confidence === 'manual' ? 'manual' : ''

    const keeperName = window.escHtml(keeper.titular || 'Sin nombre')
    const discardName = window.escHtml(discard.titular || 'Sin nombre')
    const origName = window.escHtml(origA.titular || 'Sin nombre')

    return `<div class="dup-group" id="dupGroup${idx}">
        <div class="dup-group-header">
            <div>
                <span class="dup-group-title">${origName}</span>
                <span style="font-size:11px;color:var(--text-3);margin-left:8px;">coincide por ${window.escHtml(group.matchType)}</span>
            </div>
            <span class="dup-group-badge ${confClass}">${confLabel}</span>
        </div>
        <div class="dup-direction-bar">
            <span style="font-weight:600;color:var(--green);">${keeperName} <span style="font-weight:400;color:var(--text-3);font-size:11px;">(se queda)</span></span>
            <span style="font-size:16px;color:var(--text-3);">\u2190</span>
            <button class="btn-swap" onclick="swapDupDirection(${idx})" title="Invertir direccion">\u21C4 Invertir</button>
            <span style="font-size:16px;color:var(--text-3);">\u2190</span>
            <span style="color:var(--text-3);">${discardName} <span style="font-size:11px;">(se elimina)</span></span>
        </div>
        <div class="dup-preview-grid">
            <div class="dup-preview-col dup-col-keeper">
                <div class="dup-col-header" style="color:var(--green);">\u2705 Se queda</div>
                ${fields.map(f => `<div class="dup-preview-row${f.differs ? ' diff' : ''}"><span class="dup-preview-label">${f.label}</span><span class="dup-preview-val">${window.escHtml(f.kDisplay)}</span></div>`).join('')}
            </div>
            <div class="dup-preview-col dup-col-discard">
                <div class="dup-col-header" style="color:var(--red);">\u274C Se elimina</div>
                ${fields.map(f => `<div class="dup-preview-row${f.differs ? ' diff' : ''}"><span class="dup-preview-label">${f.label}</span><span class="dup-preview-val">${window.escHtml(f.dDisplay)}</span></div>`).join('')}
            </div>
            <div class="dup-preview-col dup-col-result">
                <div class="dup-col-header" style="color:var(--purple);">\u2728 Resultado</div>
                ${fields.map(f => `<div class="dup-preview-row${f.differs ? ' diff' : ''}"><span class="dup-preview-label">${f.label}</span><span class="dup-preview-val" style="color:${sourceColor(f.source)};font-weight:600;">${window.escHtml(f.resultDisplay)}</span></div>`).join('')}
                <div style="margin-top:6px;font-size:9px;color:var(--text-3);line-height:1.4;">
                    <span style="color:var(--green);">\u25CF</span> del que se queda
                    <span style="color:var(--blue, #3B82F6);">\u25CF</span> del eliminado${isSuper ? ' <span style="color:var(--yellow);">\u25CF</span> sumado' : ''}
                </div>
            </div>
        </div>
        <div class="dup-group-actions">
            <button class="btn btn-sm btn-ghost" onclick="dismissDupGroup(${idx})">No es duplicado</button>
            ${isSuper ? `<button class="btn btn-sm btn-warning" onclick="superMergeDupGroup(${idx})" title="Fusiona y suma comisiones/valores">Super Fusionar</button>` : ''}
            <button class="btn btn-sm btn-success" onclick="mergeDupGroup(${idx})">Fusionar</button>
        </div>
    </div>`
}

function mergeDupGroup(idx, _skipSaveState) {
    const { leads, historico, clients } = store
    const group = window._dupGroups[idx]
    if (!group || group._resolved) return
    if (!_skipSaveState) window.saveState('fusionar duplicado')

    // Determine which to keep, respecting swap direction
    const [a, b] = group.leads
    const [autoKeep, autoDiscard] = _pickKeepDiscard(a, b)
    const keep = group._swapped ? autoDiscard : autoKeep
    const discard = keep === a ? b : a

    // Merge data: for each field, pick the non-empty value (best of both sides)
    const pick = (a, b) => (a && a !== 'Sin destino') ? a : b
    const pickNum = (a, b) => (a || 0) > 0 ? a : b
    keep.phone = pick(keep.phone, discard.phone)
    keep.email = pick(keep.email, discard.email)
    keep.contacto = pick(keep.contacto, discard.contacto)
    keep.travelDate = pick(keep.travelDate, discard.travelDate)
    keep.travelEnd = pick(keep.travelEnd, discard.travelEnd)
    keep.destination = pick(keep.destination, discard.destination)
    keep.reservationNum = pick(keep.reservationNum, discard.reservationNum)
    keep.hotel = pick(keep.hotel, discard.hotel)
    // Merge multi arrays: keep the longer/richer array
    if (!keep.hotels?.length && discard.hotels?.length) keep.hotels = discard.hotels
    if (!keep.parkEntries?.length && discard.parkEntries?.length) keep.parkEntries = discard.parkEntries
    if (!keep.cruises?.length && discard.cruises?.length) keep.cruises = discard.cruises
    if (!keep.grupoViajeros?.length && discard.grupoViajeros?.length) keep.grupoViajeros = discard.grupoViajeros
    keep.detalle = pick(keep.detalle, discard.detalle)
    keep.platform = pick(keep.platform, discard.platform)
    keep.observaciones = pick(keep.observaciones, discard.observaciones)
    // formData: merge richer object, then overlay arrays (prevents overwrite bug)
    const keepFdCount = keep.formData && typeof keep.formData === 'object' ? Object.values(keep.formData).filter(v => v && v !== 'nan').length : 0
    const discardFdCount = discard.formData && typeof discard.formData === 'object' ? Object.values(discard.formData).filter(v => v && v !== 'nan').length : 0
    if (discardFdCount > keepFdCount) keep.formData = { ...(discard.formData || {}), ...(keep.formData || {}) }
    if (!keep.formData) keep.formData = discard.formData || {}
    // Always persist merged arrays AFTER formData comparison
    if (keep.hotels?.length) keep.formData.hotels = keep.hotels
    if (keep.parkEntries?.length) keep.formData.parkEntries = keep.parkEntries
    if (keep.cruises?.length) keep.formData.cruises = keep.cruises
    if (keep.grupoViajeros?.length) keep.formData.grupoViajeros = keep.grupoViajeros
    // Financial: always pick the higher/non-zero value
    keep.totalVenta = pickNum(keep.totalVenta, discard.totalVenta)
    keep.commission = pickNum(keep.commission, discard.commission)
    keep.commission70 = pickNum(keep.commission70, discard.commission70)
    keep.abono = pickNum(keep.abono, discard.abono)
    keep.abonado = pickNum(keep.abonado, discard.abonado)
    keep.fechaAbono = pick(keep.fechaAbono, discard.fechaAbono)
    keep.fechaPagoFinal = pick(keep.fechaPagoFinal, discard.fechaPagoFinal)
    keep.seguimientos = Math.max(keep.seguimientos || 0, discard.seguimientos || 0)

    // Remove discard from leads/historico
    const lIdx = leads.findIndex(l => l.id === discard.id)
    if (lIdx >= 0) leads.splice(lIdx, 1)
    const hIdx = historico.findIndex(l => l.id === discard.id)
    if (hIdx >= 0) historico.splice(hIdx, 1)

    // Also remove from client trips + sync discarded client
    if (discard.clientKey && clients[discard.clientKey]) {
        const c = clients[discard.clientKey]
        const tIdx = c.trips.findIndex(t => t.id === discard.id)
        if (tIdx >= 0) c.trips.splice(tIdx, 1)
        if (discard.clientKey !== keep.clientKey) window.syncClientToSupabase(discard.clientKey, c)
    }

    // Update kept lead's client with merged data
    if (keep.clientKey && clients[keep.clientKey]) {
        const c = clients[keep.clientKey]
        c.phone = pick(c.phone, discard.phone)
        c.email = pick(c.email, discard.email)
        c.titular = pick(c.titular, discard.titular)
        c.contacto = pick(c.contacto, discard.contacto)
        // Update trip data in client too
        const trip = c.trips.find(t => t.id === keep.id)
        if (trip) {
            trip.totalVenta = keep.totalVenta; trip.commission = keep.commission; trip.commission70 = keep.commission70
            trip.travelDate = keep.travelDate; trip.travelEnd = keep.travelEnd; trip.destination = keep.destination
            trip.reservationNum = keep.reservationNum; trip.stage = keep.stage; trip.progress = keep.progress
        }
        window.syncClientToSupabase(keep.clientKey, c)
    }

    // Sync: update kept lead, delete discarded from Supabase
    window.syncLeadToSupabase(keep)
    if (discard._supabaseId) {
        store._recentSyncs.add(discard._supabaseId); setTimeout(() => store._recentSyncs.delete(discard._supabaseId), 15000)
        window.supabaseDB.deleteLead(discard._supabaseId).then(() => {
            console.log('[DupScanner] ✅ Deleted from Supabase:', discard.titular, discard._supabaseId)
        })
    } else if (discard.id) {
        // Lead might have been synced with id as supabaseId
        store._recentSyncs.add(discard.id); setTimeout(() => store._recentSyncs.delete(discard.id), 15000)
        window.supabaseDB.deleteLead(discard.id).then(() => {
            console.log('[DupScanner] ✅ Deleted from Supabase (by id):', discard.titular, discard.id)
        })
    }

    group._resolved = true
    const el = document.getElementById('dupGroup' + idx)
    if (el) {
        el.style.opacity = '0.4'
        el.style.pointerEvents = 'none'
        el.querySelector('.dup-group-actions').innerHTML = '<span style="color:var(--green);font-size:12px;font-weight:600;">Fusionado — conservado: ' + keep.titular + ' (' + (STAGES.find(s => s.id === keep.stage)?.name || keep.stage) + ')</span>'
    }
    _updateScannerCount()
}

// Super merge: same person, same trip, different financial data -> COMBINE (sum) financials
function superMergeDupGroup(idx) {
    const { leads, historico, clients } = store
    const group = window._dupGroups[idx]
    if (!group || group._resolved) return
    window.saveState('super fusionar duplicado')

    // Determine which to keep, respecting swap direction
    const [a, b] = group.leads
    const [autoKeep, autoDiscard] = _pickKeepDiscard(a, b)
    const keep = group._swapped ? autoDiscard : autoKeep
    const discard = keep === a ? b : a

    // Merge non-financial data (same as regular merge)
    const pick = (a, b) => (a && a !== 'Sin destino') ? a : b
    keep.phone = pick(keep.phone, discard.phone)
    keep.email = pick(keep.email, discard.email)
    keep.contacto = pick(keep.contacto, discard.contacto)
    keep.travelDate = pick(keep.travelDate, discard.travelDate)
    keep.travelEnd = pick(keep.travelEnd, discard.travelEnd)
    keep.destination = pick(keep.destination, discard.destination)
    keep.reservationNum = pick(keep.reservationNum, discard.reservationNum)
    keep.hotel = pick(keep.hotel, discard.hotel)
    // ⚡ SUPER: Stamp per-cruise financials from source lead before combining
    for (const cr of (keep.cruises || [])) {
        if (!cr.titular) cr.titular = keep.titular
        if (!cr.totalVenta) cr.totalVenta = keep.totalVenta || 0
        if (!cr.commission) cr.commission = keep.commission || 0
        if (!cr.abonado) cr.abonado = keep.abonado || 0
        if (cr.balance == null) cr.balance = (cr.totalVenta || 0) - (cr.abonado || 0)
    }
    for (const cr of (discard.cruises || [])) {
        if (!cr.titular) cr.titular = discard.titular
        if (!cr.totalVenta) cr.totalVenta = discard.totalVenta || 0
        if (!cr.commission) cr.commission = discard.commission || 0
        if (!cr.abonado) cr.abonado = discard.abonado || 0
        if (cr.balance == null) cr.balance = (cr.totalVenta || 0) - (cr.abonado || 0)
    }
    // ⚡ SUPER: Combine structured entries from both leads (not just pick one)
    keep.hotels = [...(keep.hotels || []), ...(discard.hotels || [])].filter((h, i, arr) =>
        i === arr.findIndex(x => x.name === h.name && x.reservationNum === h.reservationNum))
    keep.parkEntries = [...(keep.parkEntries || []), ...(discard.parkEntries || [])].filter((p, i, arr) =>
        i === arr.findIndex(x => x.park === p.park && x.reservationNum === p.reservationNum))
    keep.cruises = [...(keep.cruises || []), ...(discard.cruises || [])].filter((c, i, arr) =>
        i === arr.findIndex(x => x.line === c.line && x.reservationNum === c.reservationNum))
    // Combine grupoViajeros from both leads (tag with reservationNum for grouped display)
    const keepViajeros = keep.grupoViajeros || keep.formData?.grupoViajeros || []
    const discardViajeros = discard.grupoViajeros || discard.formData?.grupoViajeros || []
    for (const v of keepViajeros) { if (!v.reservationNum) v.reservationNum = keep.reservationNum || '' }
    for (const v of discardViajeros) { if (!v.reservationNum) v.reservationNum = discard.reservationNum || '' }
    if (discardViajeros.length > 0) {
        const combined = [...keepViajeros]
        for (const v of discardViajeros) {
            const normName = (v.nombre || '').toLowerCase().trim()
            if (normName && !combined.some(k => (k.nombre || '').toLowerCase().trim() === normName)) {
                combined.push(v)
            }
        }
        keep.grupoViajeros = combined
    }
    // Merge formData: start with richer object, then overlay merged arrays
    const keepFdCount = keep.formData && typeof keep.formData === 'object' ? Object.values(keep.formData).filter(v => v && v !== 'nan').length : 0
    const discardFdCount = discard.formData && typeof discard.formData === 'object' ? Object.values(discard.formData).filter(v => v && v !== 'nan').length : 0
    if (discardFdCount > keepFdCount) keep.formData = { ...(discard.formData || {}), ...(keep.formData || {}) }
    if (!keep.formData) keep.formData = discard.formData || {}
    // Always persist merged arrays into formData (AFTER formData merge to prevent overwrite)
    keep.formData.hotels = keep.hotels; keep.formData.parkEntries = keep.parkEntries; keep.formData.cruises = keep.cruises
    if (keep.grupoViajeros) keep.formData.grupoViajeros = keep.grupoViajeros
    // Sum viajeros count
    const keepAdults = parseInt(keep.viajeros?.match(/(\d+)A/)?.[1]) || 0
    const discardAdults = parseInt(discard.viajeros?.match(/(\d+)A/)?.[1]) || 0
    const keepMinors = parseInt(keep.viajeros?.match(/(\d+)M/)?.[1]) || 0
    const discardMinors = parseInt(discard.viajeros?.match(/(\d+)M/)?.[1]) || 0
    const totalA = keepAdults + discardAdults
    const totalM = keepMinors + discardMinors
    if (totalA > 0) keep.viajeros = `${totalA}A${totalM > 0 ? `, ${totalM}M` : ''}`
    // Type promotion: combine types if different (disney + crucero → parques_crucero, etc.)
    if (keep.type !== discard.type) {
        const types = new Set([keep.type, discard.type])
        if (types.has('crucero') && (types.has('disney') || types.has('universal'))) keep.type = 'parques_crucero'
        else if (types.has('disney') && types.has('universal')) keep.type = 'disney_universal'
    }
    keep.detalle = pick(keep.detalle, discard.detalle)
    keep.platform = pick(keep.platform, discard.platform)
    // Both reservation numbers preserved on individual cruise entries — no need for observaciones note
    keep.observaciones = pick(keep.observaciones, discard.observaciones)

    // ⚡ SUPER MERGE: SUM financials (same trip, combined bookings)
    keep.totalVenta = (keep.totalVenta || 0) + (discard.totalVenta || 0)  // Sum totals (different bookings)
    keep.commission = (keep.commission || 0) + (discard.commission || 0)  // Sum commissions
    keep.commission70 = (keep.commission70 || 0) + (discard.commission70 || 0)  // Sum M2M
    keep.abono = (keep.abono || 0) + (discard.abono || 0)  // Sum expected deposits
    keep.abonado = (keep.abonado || 0) + (discard.abonado || 0)  // Sum actual deposits
    keep.fechaAbono = pick(keep.fechaAbono, discard.fechaAbono)
    keep.fechaPagoFinal = pick(keep.fechaPagoFinal, discard.fechaPagoFinal)

    // Remove discard
    const lIdx = leads.findIndex(l => l.id === discard.id)
    if (lIdx >= 0) leads.splice(lIdx, 1)
    const hIdx = historico.findIndex(l => l.id === discard.id)
    if (hIdx >= 0) historico.splice(hIdx, 1)

    if (discard.clientKey && clients[discard.clientKey]) {
        const c = clients[discard.clientKey]
        const tIdx = c.trips.findIndex(t => t.id === discard.id)
        if (tIdx >= 0) c.trips.splice(tIdx, 1)
        if (discard.clientKey !== keep.clientKey) window.syncClientToSupabase(discard.clientKey, c)
    }
    if (keep.clientKey && clients[keep.clientKey]) {
        const c = clients[keep.clientKey]
        c.phone = pick(c.phone, discard.phone)
        c.email = pick(c.email, discard.email)
        const trip = c.trips.find(t => t.id === keep.id)
        if (trip) {
            trip.totalVenta = keep.totalVenta; trip.commission = keep.commission; trip.commission70 = keep.commission70
            trip.travelDate = keep.travelDate; trip.travelEnd = keep.travelEnd; trip.destination = keep.destination
            trip.stage = keep.stage; trip.progress = keep.progress
        }
        window.syncClientToSupabase(keep.clientKey, c)
    }

    // Sync
    window.syncLeadToSupabase(keep)
    if (discard._supabaseId) {
        store._recentSyncs.add(discard._supabaseId); setTimeout(() => store._recentSyncs.delete(discard._supabaseId), 15000)
        window.supabaseDB.deleteLead(discard._supabaseId).then(() => {
            console.log('[DupScanner] ✅ Super-deleted from Supabase:', discard.titular, discard._supabaseId)
        })
    } else if (discard.id) {
        store._recentSyncs.add(discard.id); setTimeout(() => store._recentSyncs.delete(discard.id), 15000)
        window.supabaseDB.deleteLead(discard.id)
    }

    group._resolved = true
    const el = document.getElementById('dupGroup' + idx)
    if (el) {
        el.style.opacity = '0.4'
        el.style.pointerEvents = 'none'
        el.querySelector('.dup-group-actions').innerHTML = `<span style="color:var(--yellow);font-size:12px;font-weight:600;">⚡ Super Fusionado — ${keep.titular} (Comisión: ${window.fmt(keep.commission)})</span>`
    }
    _updateScannerCount()
    window.renderAll()
}

function dismissDupGroup(idx) {
    const group = window._dupGroups[idx]
    if (!group) return
    group._resolved = true
    // Persist dismissal so this pair doesn't show again on next scan
    const dismissed = _getDismissed()
    dismissed.add(_pairKey(group.leads[0], group.leads[1]))
    _saveDismissed(dismissed)
    const el = document.getElementById('dupGroup' + idx)
    if (el) {
        el.style.opacity = '0.3'
        el.style.pointerEvents = 'none'
        el.querySelector('.dup-group-actions').innerHTML = '<span style="color:var(--text-3);font-size:12px;">Descartado</span>'
    }
    _updateScannerCount()
}

function _rescanDuplicates() {
    const { leads, historico } = store
    const allLeads = [...leads, ...historico]
    const nameMap = {}, phoneMap = {}, emailMap = {}, resNumMap = {}
    allLeads.forEach(l => {
        const nName = window.normalizeForCompare(l.titular)
        if (nName && nName !== '-') { if (!nameMap[nName]) nameMap[nName] = []; nameMap[nName].push(l) }
        const phone = (l.phone || '').replace(/\D/g, '')
        if (phone && phone.length >= 7) { if (!phoneMap[phone]) phoneMap[phone] = []; phoneMap[phone].push(l) }
        const email = (l.email || '').toLowerCase().trim()
        if (email && email.includes('@')) { if (!emailMap[email]) emailMap[email] = []; emailMap[email].push(l) }
        const resNum = (l.reservationNum || '').trim()
        if (resNum) { if (!resNumMap[resNum]) resNumMap[resNum] = []; resNumMap[resNum].push(l) }
    })
    const seen = new Set()
    const groups = []
    function addG(groupLeads, matchType) {
        const ids = groupLeads.map(l => l.id).sort().join('|')
        if (seen.has(ids)) return
        seen.add(ids)
        for (let i = 0; i < groupLeads.length; i++) {
            for (let j = i + 1; j < groupLeads.length; j++) {
                const a = groupLeads[i], b = groupLeads[j]
                const pairKey = [a.id, b.id].sort().join('|')
                if (seen.has('pair:' + pairKey)) continue
                // Check date proximity
                let daysDiff = null
                if (a.travelDate && b.travelDate) {
                    daysDiff = Math.abs(new Date(a.travelDate) - new Date(b.travelDate)) / (1000 * 60 * 60 * 24)
                    if (daysDiff > 14) continue
                }
                // Different trip types: only skip if dates are far apart (>7d) or missing
                // Close dates + different types = same trip, different components (hotel + parks + cruise)
                // Related type check: disney/disney_universal/parques_crucero are all "park" types
                const typesRelated = (t1, t2) => {
                    if (t1 === t2) return true
                    const parkTypes = ['disney', 'universal', 'disney_universal', 'parques_crucero']
                    return parkTypes.includes(t1) && parkTypes.includes(t2)
                }
                const specificTypes = ['disney', 'universal', 'crucero', 'hotel']
                const diffTypes = specificTypes.includes(a.type) && specificTypes.includes(b.type) && a.type !== b.type && !typesRelated(a.type, b.type)
                if (diffTypes && (daysDiff === null || daysDiff > 7)) continue
                // Destination compatibility: Disney family destinations are all compatible
                const destCompatible = (da, db) => {
                    if (!da || !db || da === db) return true
                    const generic = ['hotel', 'crucero', 'sin destino', 'explorando', '']
                    if (generic.includes(da) || generic.includes(db)) return true
                    const bothDisney = da.includes('disney') && db.includes('disney')
                    const bothUniversal = da.includes('universal') && db.includes('universal')
                    if (bothDisney || bothUniversal) return true
                    if (da.includes(db) || db.includes(da)) return true
                    return false
                }
                const aDestNorm = window.normalizeForCompare(a.destination)
                const bDestNorm = window.normalizeForCompare(b.destination)
                if (!destCompatible(aDestNorm, bDestNorm)) continue
                let confidence = 'medium'
                const namesMatch = window.normalizeForCompare(a.titular) === window.normalizeForCompare(b.titular)
                const sameType = a.type === b.type
                const bothNoDates = !a.travelDate && !b.travelDate
                const bothNoMoney = (!a.totalVenta || a.totalVenta === 0) && (!b.totalVenta || b.totalVenta === 0)
                const diffPrice = (a.totalVenta || 0) > 0 && (b.totalVenta || 0) > 0 && (a.totalVenta || 0) !== (b.totalVenta || 0)
                // Alta confianza REQUIRES matching names — phone/email match alone is not enough
                if (namesMatch && sameType && bothNoDates && bothNoMoney) confidence = 'high'
                if (namesMatch && sameType && a.travelDate && b.travelDate && a.travelDate === b.travelDate && !diffPrice) confidence = 'high'
                if (namesMatch && a.phone === b.phone && a.phone && !diffPrice) confidence = 'high'
                if (diffPrice || !namesMatch) confidence = 'medium'
                seen.add('pair:' + pairKey)
                groups.push({ leads: [a, b], matchType, confidence })
            }
        }
    }
    Object.values(resNumMap).filter(g => g.length > 1).forEach(g => addG(g, 'reserva'))
    Object.values(nameMap).filter(g => g.length > 1).forEach(g => addG(g, 'nombre'))
    Object.values(phoneMap).filter(g => g.length > 1).forEach(g => addG(g, 'telefono'))
    Object.values(emailMap).filter(g => g.length > 1).forEach(g => addG(g, 'email'))
    groups.sort((a, b) => (b.confidence === 'high' ? 1 : 0) - (a.confidence === 'high' ? 1 : 0))
    return groups
}

function _closeScannerUI() {
    const overlay = document.getElementById('dupScannerOverlay')
    if (overlay) overlay.remove()
    window._dupGroups = null
}

function _isDiffPrice(g) {
    const [a, b] = g.leads
    const aVal = a.totalVenta || 0
    const bVal = b.totalVenta || 0
    return aVal !== bVal && aVal > 0 && bVal > 0
}

// Broader check: are these separate bookings for the same trip?
// Same name + similar dates + any of: different types, different reservation#, different commissions
function _isSuperMergeCandidate(g) {
    if (_isDiffPrice(g)) return true
    const [a, b] = g.leads
    // Different types (hotel + crucero, disney + hotel, etc.)
    if (a.type && b.type && a.type !== b.type) return true
    // Different reservation numbers (separate bookings)
    if (a.reservationNum && b.reservationNum && a.reservationNum !== b.reservationNum) return true
    // Different commissions (even if venta is $0 on both)
    const aCom = a.commission || 0
    const bCom = b.commission || 0
    if (aCom > 0 && bCom > 0 && aCom !== bCom) return true
    // Both have commissions (even if same amount — could be separate bookings)
    if (aCom > 0 && bCom > 0 && a.type !== b.type) return true
    return false
}

function dismissDiffPrice() {
    if (!window._dupGroups) return
    let totalDismissed = 0
    window._dupGroups.forEach((g, idx) => {
        if (!g._resolved && _isSuperMergeCandidate(g)) {
            dismissDupGroup(idx)
            totalDismissed++
        }
    })
    if (totalDismissed > 0) {
        window.toast(`${totalDismissed} pares descartados (precio diferente)`, 'info')
    }
    // Update remaining count in header
    _updateScannerCount()
}

function _updateScannerCount() {
    const remaining = (window._dupGroups || []).filter(g => !g._resolved).length
    const countEl = document.getElementById('dupScannerRemaining')
    if (countEl) countEl.textContent = remaining + ' pendiente(s)'
    if (remaining === 0) {
        window.toast('Todos los duplicados procesados', 'success')
    }
}

function superMergeAll() {
    if (!window._dupGroups) return
    window.saveState('super fusionar todos')
    let totalMerged = 0
    let pass = 0

    while (pass++ < 10) {
        let merged = 0
        window._dupGroups.forEach((g, idx) => {
            if (!g._resolved && _isSuperMergeCandidate(g)) { superMergeDupGroup(idx); merged++ }
        })
        totalMerged += merged
        if (merged === 0) break

        // Re-scan — merging can create new groups (e.g. 3 leads for same person)
        _closeScannerUI()
        const newGroups = _rescanDuplicates()
        if (!newGroups.some(g => _isSuperMergeCandidate(g))) {
            if (newGroups.length > 0) showDupScanner(newGroups)
            break
        }
        window._dupGroups = newGroups
    }

    if (totalMerged > 0) {
        window.toast(`⚡ ${totalMerged} leads super-fusionados (${pass} pasada${pass > 1 ? 's' : ''})`, 'success')
        window.renderAll()
    }
}

function mergeHighConfidence() {
    if (!window._dupGroups) return
    window.saveState('fusionar duplicados alta confianza')
    let totalMerged = 0
    let pass = 0

    while (pass++ < 10) {
        let merged = 0
        window._dupGroups.forEach((g, idx) => {
            // Skip diff-price groups (those are for super merge)
            if (!g._resolved && g.confidence === 'high' && !_isSuperMergeCandidate(g)) { mergeDupGroup(idx, true); merged++ }
        })
        totalMerged += merged
        if (merged === 0) break

        _closeScannerUI()
        const newGroups = _rescanDuplicates()
        if (!newGroups.some(g => g.confidence === 'high')) {
            if (newGroups.length > 0) showDupScanner(newGroups)
            break
        }
        window._dupGroups = newGroups
    }

    if (pass >= 10) {
        window.toast(`Limite de 10 pasadas alcanzado — revisar duplicados restantes manualmente`, 'warning')
    }
    if (totalMerged > 0) {
        window.toast(`${totalMerged} duplicados de alta confianza fusionados (${pass} pasada${pass > 1 ? 's' : ''})`, 'success')
        window.renderAll()
    }
}

function mergeAllDuplicates() {
    if (!window._dupGroups) return
    window.saveState('fusionar todos los duplicados')
    let merged = 0
    window._dupGroups.forEach((g, idx) => {
        if (!g._resolved) { mergeDupGroup(idx, true); merged++ }
    })
    if (merged > 0) {
        window.toast(`${merged} duplicados fusionados`, 'success')
        window.renderAll()
    }
}

function closeDupScanner() {
    const overlay = document.getElementById('dupScannerOverlay')
    if (overlay) overlay.remove()
    // Check if any were resolved
    if (window._dupGroups && window._dupGroups.some(g => g._resolved)) {
        // Clean dismissed pairs referencing leads that no longer exist
        const { leads, historico } = store
        const allIds = new Set([...leads, ...historico].map(l => l.id))
        const dismissed = _getDismissed()
        const cleaned = new Set([...dismissed].filter(key => {
            const [idA, idB] = key.split('|')
            return allIds.has(idA) && allIds.has(idB)
        }))
        if (cleaned.size !== dismissed.size) _saveDismissed(cleaned)
        window.renderAll()
    }
    window._dupGroups = null
}

function fixConsentTextDestinations() {
    const { leads, historico } = store
    // One-time cleanup: leads imported with Parques form misdetected as Hoteles
    // had T&C consent text as destination and formData. Fix them.
    if (window._consentCleanupDone) return
    window._consentCleanupDone = true
    const consentPattern = /entiendo las pol[ií]ticas|acepto el recargo|terminos y condiciones|proceso de cotizaci[oó]n/i
    let fixed = 0
    ;[...leads, ...historico].forEach(l => {
        let dirty = false
        // Fix destination field
        if (l.destination && consentPattern.test(l.destination)) {
            l.destination = 'Disney World'
            if (l.type === 'hotel') l.type = 'disney'
            dirty = true
        }
        // Fix formData.destino (stored as JSONB)
        if (l.formData && typeof l.formData === 'object' && l.formData.destino && consentPattern.test(l.formData.destino)) {
            l.formData.destino = l.destination || 'Disney World'
            dirty = true
        }
        // Fix formData stored as string
        if (l.formData && typeof l.formData === 'string') {
            try { l.formData = JSON.parse(l.formData) } catch(e) {}
            if (l.formData && l.formData.destino && consentPattern.test(l.formData.destino)) {
                l.formData.destino = l.destination || 'Disney World'
                dirty = true
            }
        }
        // Fix observaciones that contain T&C text as destination
        if (l.observaciones && consentPattern.test(l.observaciones)) {
            l.observaciones = l.observaciones.replace(/Destino:.*?(Entiendo las pol[ií]ticas[^\n]*)/gi, 'Destino: Disney World')
            dirty = true
        }
        if (dirty) { fixed++; window.syncLeadToSupabase(l) }
    })
    if (fixed > 0) {
        window.toast(`${fixed} leads corregidos (datos Typeform)`, 'info')
    }
}

function filterDupGroups(query) {
    const norm = window.normalizeForCompare(query)
    const groups = window._dupGroups || []
    let shown = 0
    groups.forEach((g, idx) => {
        const el = document.getElementById('dupGroup' + idx)
        if (!el || g._resolved) return
        if (!norm) { el.style.display = ''; shown++; return }
        const [a, b] = g.leads
        const haystack = [a.titular, b.titular, a.destination, b.destination, a.phone, b.phone, a.email, b.email]
            .map(v => window.normalizeForCompare(v || '')).join(' ')
        const match = haystack.includes(norm)
        el.style.display = match ? '' : 'none'
        if (match) shown++
    })
    const countEl = document.getElementById('dupSearchCount')
    if (countEl) {
        if (norm) {
            countEl.style.display = 'block'
            countEl.textContent = `Mostrando ${shown} de ${groups.filter(g => !g._resolved).length} grupos`
        } else {
            countEl.style.display = 'none'
        }
    }
}

function manualMerge(leadIdA, leadIdB) {
    const { leads, historico } = store
    const allLeads = [...leads, ...historico]
    const a = allLeads.find(l => l.id === leadIdA)
    const b = allLeads.find(l => l.id === leadIdB)
    if (!a || !b) { window.toast('No se encontraron los leads seleccionados', 'error'); return }

    // Create synthetic group for the comparison modal
    const group = { leads: [a, b], matchType: 'manual', confidence: 'manual' }
    window._dupGroups = [group]

    // Remove existing overlay
    const existing = document.getElementById('dupScannerOverlay')
    if (existing) existing.remove()

    const overlay = document.createElement('div')
    overlay.className = 'dup-scanner-overlay'
    overlay.id = 'dupScannerOverlay'
    overlay.innerHTML = `
        <div class="dup-scanner">
            <div class="dup-scanner-header">
                <h3>Fusionar Leads</h3>
                <button class="btn btn-ghost btn-sm" onclick="closeDupScanner()" style="font-size:20px;">&times;</button>
            </div>
            <div class="dup-scanner-body" id="dupScannerBody">
                ${renderDupGroup(group, 0)}
            </div>
            <div class="dup-scanner-footer">
                <span style="font-size:12px;color:var(--text-3);">Selección manual — revisa los datos antes de fusionar</span>
                <button class="btn btn-sm btn-ghost" onclick="closeDupScanner()">Cancelar</button>
            </div>
        </div>`
    document.body.appendChild(overlay)
}

Object.assign(window, {
    scanDuplicates, showDupScanner, renderDupGroup,
    mergeDupGroup, dismissDupGroup, _rescanDuplicates,
    _closeScannerUI, _isDiffPrice, _isSuperMergeCandidate,
    dismissDiffPrice, mergeHighConfidence, mergeAllDuplicates, superMergeAll,
    closeDupScanner, fixConsentTextDestinations, superMergeDupGroup,
    filterDupGroups, manualMerge, swapDupDirection,
})
